import { useMutation, useQueryClient, type InfiniteData, type QueryKey } from "@tanstack/react-query";
import { toast as sonnerToast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentOrgId, useCurrentUser } from "../auth";
import { toast } from "@/hooks/use-toast";
import {
  notificationsCountKey,
  notificationsListPrefix,
  type Notification,
  type NotificationsListKeyOptions,
} from "./useNotifications";

type ListData = InfiniteData<Notification[], number>;

// Timers de exclusão com undo pendente. Em module scope de propósito: o item some
// da lista na hora (optimistic), o componente desmonta, mas a mutation adiada
// precisa sobreviver até o timer disparar ou o usuário desfazer.
const pendingDeletes = new Map<string, ReturnType<typeof setTimeout>>();

interface CacheSnapshot {
  lists: Array<[QueryKey, ListData | undefined]>;
  count: number | undefined;
}

/**
 * Mutations de notificação com optimistic updates sobre TODAS as variantes de
 * lista (abas) via prefixo ["notifications", orgId, userId]. Não monta nenhuma
 * query — seguro de usar por item renderizado.
 */
export function useNotificationActions() {
  const { orgId } = useCurrentOrgId();
  const { user } = useCurrentUser();
  const userId = user?.user_id;
  const queryClient = useQueryClient();

  const listPrefix = notificationsListPrefix(orgId, userId);
  const countKey = notificationsCountKey(orgId, userId);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: listPrefix });
    queryClient.invalidateQueries({ queryKey: countKey });
  };

  const cancelQueries = async () => {
    await Promise.all([
      queryClient.cancelQueries({ queryKey: listPrefix }),
      queryClient.cancelQueries({ queryKey: countKey }),
    ]);
  };

  const snapshot = (): CacheSnapshot => ({
    lists: queryClient.getQueriesData<ListData>({ queryKey: listPrefix }),
    count: queryClient.getQueryData<number>(countKey),
  });

  const restore = (snap: CacheSnapshot) => {
    for (const [key, data] of snap.lists) queryClient.setQueryData(key, data);
    if (snap.count !== undefined) queryClient.setQueryData(countKey, snap.count);
  };

  /**
   * Aplica um transform por item em todas as listas cacheadas. Retornar null
   * remove o item da lista; o transform recebe as opções da variante (a key
   * carrega { unreadOnly, categories }) para decidir remover vs. atualizar.
   */
  const mapLists = (
    fn: (n: Notification, opts: NotificationsListKeyOptions) => Notification | null
  ) => {
    for (const [key, data] of queryClient.getQueriesData<ListData>({ queryKey: listPrefix })) {
      if (!data?.pages) continue;
      const opts = (key[3] ?? { unreadOnly: false, categories: null }) as NotificationsListKeyOptions;
      queryClient.setQueryData<ListData>(key, {
        ...data,
        pages: data.pages.map((page) =>
          page.flatMap((n) => {
            const next = fn(n, opts);
            return next ? [next] : [];
          })
        ),
      });
    }
  };

  const bumpCount = (delta: number) => {
    queryClient.setQueryData<number>(countKey, (c) => Math.max(0, (c ?? 0) + delta));
  };

  const markAsReadMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await supabase
        .from("notifications")
        .update({ is_read: true })
        .eq("id", notificationId);
      if (error) throw error;
    },
    onMutate: async (notificationId: string) => {
      await cancelQueries();
      const snap = snapshot();
      let wasUnread = false;
      mapLists((n, opts) => {
        if (n.id !== notificationId) return n;
        if (!n.is_read) wasUnread = true;
        return opts.unreadOnly ? null : { ...n, is_read: true };
      });
      if (wasUnread) bumpCount(-1);
      return snap;
    },
    onError: (_err, _id, snap) => {
      if (snap) restore(snap);
      toast({ title: "Erro", description: "Falha ao marcar notificação como lida", variant: "destructive" });
    },
    onSettled: invalidate,
  });

  const markAsUnreadMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      // O trigger update_notification_read_at zera read_at quando is_read volta a false.
      const { error } = await supabase
        .from("notifications")
        .update({ is_read: false })
        .eq("id", notificationId);
      if (error) throw error;
    },
    onMutate: async (notificationId: string) => {
      await cancelQueries();
      const snap = snapshot();
      let wasRead = false;
      // Nas listas unreadOnly o item não está presente; o invalidate do
      // onSettled o traz de volta na posição certa.
      mapLists((n, opts) => {
        if (n.id !== notificationId || opts.unreadOnly) return n;
        if (n.is_read) wasRead = true;
        return { ...n, is_read: false };
      });
      if (wasRead) bumpCount(1);
      return snap;
    },
    onError: (_err, _id, snap) => {
      if (snap) restore(snap);
      toast({ title: "Erro", description: "Falha ao marcar notificação como não lida", variant: "destructive" });
    },
    onSettled: invalidate,
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      if (!orgId || !userId) return;
      const { error } = await supabase
        .from("notifications")
        .update({ is_read: true })
        .eq("org_id", orgId)
        .eq("user_id", userId)
        .eq("is_read", false);
      if (error) throw error;
    },
    onMutate: async () => {
      await cancelQueries();
      const snap = snapshot();
      mapLists((n, opts) => (opts.unreadOnly ? null : { ...n, is_read: true }));
      queryClient.setQueryData<number>(countKey, 0);
      return snap;
    },
    onSuccess: () => {
      toast({ title: "Pronto", description: "Todas as notificações foram marcadas como lidas" });
    },
    onError: (_err, _vars, snap) => {
      if (snap) restore(snap);
      toast({ title: "Erro", description: "Falha ao marcar todas como lidas", variant: "destructive" });
    },
    onSettled: invalidate,
  });

  /**
   * Exclui com janela de desfazer: remove da UI na hora, mostra toast (Sonner —
   * o toast shadcn tem TOAST_LIMIT=1 e seria engolido por um toast de realtime)
   * e só dispara o DELETE real após 5s se o usuário não desfizer.
   */
  const deleteWithUndo = (notification: Notification) => {
    const id = notification.id;
    if (pendingDeletes.has(id)) return;

    mapLists((n) => (n.id === id ? null : n));
    if (!notification.is_read) bumpCount(-1);

    const undo = () => {
      const timer = pendingDeletes.get(id);
      if (timer === undefined) return;
      clearTimeout(timer);
      pendingDeletes.delete(id);
      // O item volta do servidor — não precisamos restaurar snapshot.
      invalidate();
    };

    const timer = setTimeout(async () => {
      pendingDeletes.delete(id);
      const { error } = await supabase.from("notifications").delete().eq("id", id);
      if (error) {
        toast({ title: "Erro", description: "Falha ao excluir notificação", variant: "destructive" });
        invalidate();
      }
    }, 5200);
    pendingDeletes.set(id, timer);

    sonnerToast("Notificação excluída", {
      action: { label: "Desfazer", onClick: undo },
      duration: 5000,
    });
  };

  const clearReadMutation = useMutation({
    mutationFn: async (): Promise<number | null> => {
      // RPC server-side: sem o teto de linhas do PostgREST e com contagem exata.
      const { data, error } = await supabase.rpc("clear_read_notifications");
      if (!error) return data ?? null;
      // Fallback enquanto a RPC não estiver aplicada em prod (PGRST202 =
      // função inexistente): DELETE direto — funciona sob a RLS de DELETE,
      // apenas sem contagem no retorno.
      if (error.code === "PGRST202") {
        if (!orgId || !userId) throw error;
        const { error: delError } = await supabase
          .from("notifications")
          .delete()
          .eq("org_id", orgId)
          .eq("user_id", userId)
          .eq("is_read", true);
        if (delError) throw delError;
        return null;
      }
      throw error;
    },
    onMutate: async () => {
      await cancelQueries();
      const snap = snapshot();
      mapLists((n, opts) => (opts.unreadOnly || !n.is_read ? n : null));
      return snap;
    },
    onSuccess: (count) => {
      toast({
        title: "Pronto",
        description:
          count && count > 0
            ? `${count} notificaç${count > 1 ? "ões lidas excluídas" : "ão lida excluída"}`
            : "Notificações lidas excluídas",
      });
    },
    onError: (_err, _vars, snap) => {
      if (snap) restore(snap);
      toast({ title: "Erro", description: "Falha ao limpar notificações lidas", variant: "destructive" });
    },
    onSettled: invalidate,
  });

  return {
    markAsRead: markAsReadMutation.mutate,
    markAsUnread: markAsUnreadMutation.mutate,
    markAllAsRead: markAllAsReadMutation.mutate,
    deleteWithUndo,
    clearRead: clearReadMutation.mutate,
    isMarkingAllAsRead: markAllAsReadMutation.isPending,
    isClearingRead: clearReadMutation.isPending,
  };
}
